import {
  getBuildVersion,
  getGitDate,
  getGitHash,
} from '../utils/buildVersion' with { type: 'macro' };
import { closeDialog } from '@/stores/dialog';
import { BUILD_PLATFORM, IS_TAURI } from '@/utils/platformUtils';
import {
  getVersion as _getTauriAppVersion,
  getTauriVersion as _getTauriVersion,
} from '@tauri-apps/api/app';
import { useState } from 'react';

async function getTauriVersion(): Promise<string> {
  return _getTauriVersion().catch(() => 'unknown');
}

async function getTauriAppVersion(): Promise<string> {
  return _getTauriAppVersion().catch(() => 'unknown');
}

const buildVersion = await getBuildVersion();
const tauriVersion = await getTauriVersion();
const tauriAppVersion = await getTauriAppVersion();
const gitHash = await getGitHash();
const buildDate = new Date(await getGitDate());

function getDebugInfo() {
  return {
    version: buildVersion,
    tauriVersion,
    tauriAppVersion,
    buildPlatform: BUILD_PLATFORM,
    gitCommit: gitHash,
    buildDate: buildDate.toISOString(),
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    language: navigator.language,
    languages: navigator.languages.join(', '),
    screenResolution: `${window.screen.width}x${window.screen.height}`,
    viewportSize: `${window.innerWidth}x${window.innerHeight}`,
    colorDepth: window.screen.colorDepth,
    pixelRatio: window.devicePixelRatio,
    online: navigator.onLine,
    cookiesEnabled: navigator.cookieEnabled,
    serviceWorkerSupported: 'serviceWorker' in navigator,
    serviceWorkerActive: navigator.serviceWorker?.controller ? 'Yes' : 'No',
    indexedDBSupported: 'indexedDB' in window,
    localStorageSupported: typeof Storage !== 'undefined',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    timestamp: new Date().toISOString(),
    // Additional useful info
    memory: (performance as any).memory
      ? {
          usedJSHeapSize:
            Math.round((performance as any).memory.usedJSHeapSize / 1048576) +
            'MB',
          totalJSHeapSize:
            Math.round((performance as any).memory.totalJSHeapSize / 1048576) +
            'MB',
        }
      : 'Not available',
    connection: (navigator as any).connection
      ? {
          effectiveType: (navigator as any).connection.effectiveType,
          downlink: (navigator as any).connection.downlink + 'Mbps',
          rtt: (navigator as any).connection.rtt + 'ms',
        }
      : 'Not available',
  };
}

export type DebugInfo = Awaited<ReturnType<typeof getDebugInfo>>;

function formatDebugInfo(info: DebugInfo) {
  const memoryInfo =
    typeof info.memory === 'object'
      ? `Used: ${info.memory.usedJSHeapSize}, Total: ${info.memory.totalJSHeapSize}`
      : info.memory;

  const connectionInfo =
    typeof info.connection === 'object'
      ? `Type: ${info.connection.effectiveType}, Speed: ${info.connection.downlink}, RTT: ${info.connection.rtt}`
      : info.connection;

  return `**Logbook Debug Info**

**Build Info**
Version: ${info.version}
Git Commit: ${info.gitCommit}
Build Date: ${info.buildDate}
Build Platform: ${info.buildPlatform}

**Tauri Info**
Version: ${info.tauriVersion}
App Version: ${info.tauriAppVersion}

**Browser Info**
User Agent: ${info.userAgent}
Platform: ${info.platform}
Language: ${info.language}
Languages: ${info.languages}

**Display**
Screen Resolution: ${info.screenResolution}
Viewport Size: ${info.viewportSize}
Color Depth: ${info.colorDepth}
Pixel Ratio: ${info.pixelRatio}

**Features**
Online: ${info.online}
Cookies: ${info.cookiesEnabled}
Service Worker Supported: ${info.serviceWorkerSupported}
Service Worker Active: ${info.serviceWorkerActive}
IndexedDB: ${info.indexedDBSupported}
LocalStorage: ${info.localStorageSupported}

**Performance**
Memory: ${memoryInfo}
Connection: ${connectionInfo}

**System**
Timezone: ${info.timezone}
Timestamp: ${info.timestamp}`;
}

export function AboutDialog() {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    const debugInfo = await getDebugInfo();
    const formattedInfo = formatDebugInfo(debugInfo);

    try {
      await navigator.clipboard.writeText(formattedInfo);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = formattedInfo;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const debugInfo = getDebugInfo();

  return (
    <div className="stop-scrolling fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded bg-white p-6 shadow-lg">
        <h2 className="mb-4 text-2xl font-bold">About Logbook</h2>
        <p className="mb-4">
          Logbook is an application for managing and exporting radio logs.
        </p>

        <div className="mb-4 rounded bg-gray-100 p-4">
          <h3 className="mb-2 font-semibold">Version Information</h3>
          <p className="text-sm">Version: {buildVersion}</p>
          <p className="text-sm">Git Commit: {gitHash}</p>
          <p className="text-sm">
            Build Date: {buildDate.toISOString().split('T')[0]}{' '}
            {buildDate.toTimeString().split(' ')[0]}
          </p>
          {IS_TAURI && (
            <>
              <p className="text-sm">Tauri Version: {tauriVersion}</p>
              <p className="text-sm">Tauri App Version: {tauriAppVersion}</p>
            </>
          )}
        </div>

        <div className="mb-4 rounded bg-gray-100 p-4">
          <h3 className="mb-2 font-semibold">System Information</h3>
          <p className="text-sm">
            Browser:{' '}
            {debugInfo.userAgent
              .substring(debugInfo.userAgent.lastIndexOf(')') + 1)
              .trim()}
          </p>
          <p className="text-sm">Platform: {debugInfo.platform}</p>
          <p className="text-sm">Screen: {debugInfo.screenResolution}</p>
          <p className="text-sm">Timezone: {debugInfo.timezone}</p>
        </div>

        <div className="flex gap-2">
          <button
            className="cursor-pointer rounded bg-green-600 px-4 py-2 text-white hover:bg-green-700"
            onClick={handleCopy}
          >
            {copied ? '✓ Copied!' : 'Copy Debug Info'}
          </button>
          <button
            className="cursor-pointer rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
            onClick={closeDialog}
          >
            Close
          </button>
        </div>

        <p className="mt-4 text-xs text-gray-600">
          Debug info includes system and browser details to help with issue
        </p>

        <p className="mt-2 text-xs text-gray-600">
          Made with passion by{' '}
          <a
            href="https://shoghisimon.ca/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline"
          >
            Simon
          </a>
        </p>
      </div>
    </div>
  );
}
